// How many weeks (with competitor data) to draw on each side of the selected
// week on the trend chart, and the gap (in occupancy fraction) at which your
// fleet reads as ahead of / behind the market for the selected week.

/** Structural subset of a calendar week that market-status math needs.
 *  Satisfied by both the comp-set CalendarWeek and the dashboard's
 *  MarketStatusWeek (from @sea-snag/shared). */
export interface OccupancyWeek {
  weekStart: string;
  totalCompetitors: number;
  bookedCount: number;
  occupancyRate: number | null;
  ownTotal: number;
  ownBookedCount: number;
  ownOccupancyRate: number | null;
}

export const TREND_SIDE_WEEKS = 2;
export const LEAD_DELTA = 0.08;

export type MarketVerdict = 'ahead' | 'inline' | 'behind' | 'none';

export interface TrendPoint {
  weekStart: string; // ISO date of the charter week
  marketRate: number; // 0..1 competitor occupancy
  ownRate: number | null; // 0..1 own-fleet occupancy; null when no reference data that week
  isSelected: boolean; // the currently-selected week
}

export interface MarketStatus {
  marketRate: number | null; // selected-week competitor occupancy (0..1)
  ownRate: number | null; // selected-week own-fleet occupancy (0..1)
  bookedCount: number; // competitors booked this week
  totalCompetitors: number;
  ownBookedCount: number; // own boats booked this week
  ownTotal: number; // org's own (reference) boats in the set
  verdict: MarketVerdict; // own vs market for the selected week; 'none' = no competitor data, or competitor data but no own/reference data
  delta: number | null; // ownRate - marketRate (fraction), null when either side missing
  trend: TrendPoint[]; // oldest->newest, incl. selected week (isSelected: true)
}

/**
 * Reads the selected week's competitor occupancy and compares the org's own
 * fleet against it. Cross-week occupancy is lead-time biased (nearer weeks book
 * more), so the verdict compares own vs market at the SAME week (apples-to-apples)
 * rather than reading momentum off the trend slope.
 */
export function deriveMarketStatus(calendar: OccupancyWeek[], selectedIndex: number): MarketStatus {
  const sel = calendar[selectedIndex];
  if (!sel || sel.occupancyRate == null) {
    return {
      marketRate: null,
      ownRate: sel?.ownOccupancyRate ?? null,
      bookedCount: sel?.bookedCount ?? 0,
      totalCompetitors: sel?.totalCompetitors ?? 0,
      ownBookedCount: sel?.ownBookedCount ?? 0,
      ownTotal: sel?.ownTotal ?? 0,
      verdict: 'none',
      delta: null,
      trend: [],
    };
  }

  // A window centred on the selected week: up to TREND_SIDE_WEEKS weeks with
  // competitor data on each side, so the selected week sits in the middle of the
  // chart rather than at the far edge.
  const toPoint = (w: OccupancyWeek): TrendPoint => ({
    weekStart: w.weekStart,
    marketRate: w.occupancyRate as number, // caller guards occupancyRate != null
    ownRate: w.ownOccupancyRate,
    isSelected: false,
  });

  // Earlier weeks, newest-first, then reversed to oldest->newest.
  const before: TrendPoint[] = [];
  for (let i = selectedIndex - 1; i >= 0 && before.length < TREND_SIDE_WEEKS; i -= 1) {
    const w = calendar[i];
    if (w?.occupancyRate != null) before.push(toPoint(w));
  }
  // Later weeks, oldest-first.
  const after: TrendPoint[] = [];
  for (let i = selectedIndex + 1; i < calendar.length && after.length < TREND_SIDE_WEEKS; i += 1) {
    const w = calendar[i];
    if (w?.occupancyRate != null) after.push(toPoint(w));
  }

  const trend: TrendPoint[] = [
    ...[...before].reverse(),
    {
      weekStart: sel.weekStart,
      marketRate: sel.occupancyRate,
      ownRate: sel.ownOccupancyRate,
      isSelected: true,
    },
    ...after,
  ];

  // Positional verdict: own vs market for the selected week. Only when the org
  // has reference-boat occupancy data for that week.
  let verdict: MarketVerdict = 'none';
  let delta: number | null = null;
  if (sel.ownOccupancyRate != null) {
    delta = sel.ownOccupancyRate - sel.occupancyRate;
    verdict = delta >= LEAD_DELTA ? 'ahead' : delta <= -LEAD_DELTA ? 'behind' : 'inline';
  }

  return {
    marketRate: sel.occupancyRate,
    ownRate: sel.ownOccupancyRate,
    bookedCount: sel.bookedCount,
    totalCompetitors: sel.totalCompetitors,
    ownBookedCount: sel.ownBookedCount,
    ownTotal: sel.ownTotal,
    verdict,
    delta,
    trend,
  };
}
